import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../css/AminiiLoginStyle.css";
import { API_BASE_URL } from "../config/constants";

function LoginPage() {
  const [loginMethod, setLoginMethod] = useState("email"); // Default to email login
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [logoutMessage, setLogoutMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [animationStage, setAnimationStage] = useState(0); // 0: none, 1: loading, 2: success
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  useEffect(() => {
    const reason = localStorage.getItem("logoutReason");
    if (reason === "inactivity") {
      setLogoutMessage("You have been logged out due to inactivity.");
      localStorage.removeItem("logoutReason");
    }
  }, []);

  const decodeJwtPayload = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  const findTokenInResponse = (data) => {
    const tokenFields = [
      "token",
      "accessToken",
      "jwt",
      "jwtToken",
      "authToken",
      "access_token",
      "bearerToken",
    ];

    for (const field of tokenFields) {
      if (data?.[field]) return data[field];
    }
    if (data?.data) {
      for (const field of tokenFields) {
        if (data.data?.[field]) return data.data[field];
      }
    }
    if (data?.auth) {
      for (const field of tokenFields) {
        if (data.auth?.[field]) return data.auth[field];
      }
    }
    return null;
  };

  const handleLoginMethodSelect = (method) => {
    setLoginMethod(method);
    setError("");
    setEmail("");
    setMobile("");
    setPassword("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      setAnimationStage(1);
      setShowPopup(true);

      const loginPayload = { userEmail: email, userPassword: password };
      if (mobile) loginPayload.userMobile = mobile;

      const loginResponse = await axios.post(
        `${API_BASE_URL}/login`,
        loginPayload,
        { withCredentials: true }
      );

      // API wraps response under { data: { accessToken, refreshToken, userId, firstName, role } }
      const responseData = loginResponse.data?.data || loginResponse.data;
      const token =
        responseData.accessToken ||
        responseData.jwt ||
        findTokenInResponse(loginResponse.data);
      if (!token) throw new Error("No valid token found in response");

      const storedRefreshToken = responseData.refreshToken;
      const userId = responseData.userId;
      const firstName = responseData.firstName || "";
      const userRole = responseData.role || responseData.userRole;

      localStorage.setItem("token", token);
      if (storedRefreshToken) localStorage.setItem("refreshToken", storedRefreshToken);
      if (userId) localStorage.setItem("userId", String(userId));
      localStorage.setItem("userFirstName", firstName);
      localStorage.setItem("userEmail", email);
      localStorage.setItem("isLoggedIn", "true");
      if (userRole) localStorage.setItem("userRole", String(userRole));

      setTimeout(() => {
        setAnimationStage(2);
      }, 1000);

      setTimeout(() => {
        setShowPopup(false);
        navigate(userRole?.toLowerCase().includes("admin") ? "/AdminAminii" : "/");
      }, 4000);
    } catch (err) {
      setShowPopup(false);
      setAnimationStage(0);

      if (err.response?.status === 401) {
        setError("Invalid email or password.");
      } else if (err.response?.status === 403) {
        setError("Access forbidden. Please check your credentials.");
      } else if (err.response?.status === 400) {
        setError("Invalid email or password.");
      } else if (err.response?.status === 500) {
        setError("Server error. Please try again later.");
      } else {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Login failed. Please try again."
        );
      }
    }
  };

  return (
    <>
    <div className="login-container">
      <div className="login-box">
        <button className="auth-back-btn" onClick={() => navigate("/")} type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          Explore without login
        </button>
        <div className="brand-section" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <h1 className="brand-name">A M I N I I</h1>
          <p className="brand-subtitle">C L O T H I N G</p>
        </div>

        {logoutMessage && <p className="info-message">{logoutMessage}</p>}
        <form onSubmit={handleLogin} className="login-form">
          {loginMethod === "email" ? (
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                className="input-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="mobile">Mobile Number</label>
              <input
                type="tel"
                id="mobile"
                className="input-field"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
                placeholder="e.g., 9876543210"
                pattern="[0-9]{10}"
                title="Please enter a 10-digit mobile number"
                disabled
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className="input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                className="password-toggle-icon"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <div className="forgot-password">
            <a
              href="/forgotPassword"
              onClick={() => navigate("/forgotPassword")}
            >
              Forgot Password?
            </a>
          </div>

          {error && <p className="error-message">{error}</p>}

          <button type="submit" className="login-button">
            Login
          </button>
        </form>

        <p className="signup-link">
          Don't have an account?{" "}
          <a href="/register" onClick={() => navigate("/register")}>
            Sign Up
          </a>
        </p>
      </div>

      <div className="decorative-circle decorative-circle-1"></div>
      <div className="decorative-circle decorative-circle-2"></div>
      <div className="decorative-circle decorative-circle-3"></div>
    </div>

    {showPopup && ReactDOM.createPortal(
      <div className="amin-login-portal-overlay">
        <div className="login-popup-content">
          {animationStage === 1 && (
            <div className="loading-animation">
              <div className="spinner"></div>
              <div className="clothing-items">
                <div className="clothing-item item1"></div>
                <div className="clothing-item item2"></div>
                <div className="clothing-item item3"></div>
              </div>
              <h2>Logging in...</h2>
              <p>Preparing your fashion journey</p>
            </div>
          )}

          {animationStage === 2 && (
            <div className="success-animation-container">
              <div className="success-checkmark">
                <div className="check-icon">
                  <span className="icon-line line-tip"></span>
                  <span className="icon-line line-long"></span>
                  <div className="icon-circle"></div>
                  <div className="icon-fix"></div>
                </div>
              </div>
              <div className="fashion-runway">
                <div className="runway-model"></div>
                <div className="runway-lights"></div>
              </div>
              <h2 className="login-success-title">Welcome Back!</h2>
              <p className="login-success-message">
                Your style journey continues...
              </p>
            </div>
          )}
        </div>
      </div>,
      document.body
    )}
    </>
  );
}

export default LoginPage;
